import { Tool } from '@modelcontextprotocol/sdk/types.js';
import {
  AnalyzeCodeQualityInputSchema,
  type Config,
  type CodeQualityResult,
  type QualitySummary,
  type MetricResults,
  type MetricStat,
  type Hotspot,
  type FunctionMetrics,
  type UnusedMember,
  type DuplicatedString,
  type UnassignedField,
} from '../types/index.js';
import { findFiles, readText } from '../utils/fs.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { logger } from '../lib/logger.js';
import path from 'path';

export const analyzeCodeQualityTool: Tool = {
  name: 'analyze_code_quality',
  description: 'Analyze code quality metrics for SmartStack projects: cognitive complexity, cyclomatic complexity, function size, nesting depth, unused members (S1144), duplicated strings (S1192), and unassigned fields (S3459).',
  inputSchema: {
    type: 'object',
    properties: {
      path: {
        type: 'string',
        description: 'Project path to analyze (default: SmartStack.app path from config)',
      },
      metrics: {
        type: 'array',
        items: {
          type: 'string',
          enum: [
            'cognitive-complexity',
            'cyclomatic-complexity',
            'function-size',
            'nesting-depth',
            'parameter-count',
            'code-duplication',
            'file-size',
            'unused-members',
            'duplicated-strings',
            'unassigned-fields',
            'all',
          ],
        },
        description: 'Metrics to analyze',
        default: ['all'],
      },
      threshold: {
        type: 'string',
        enum: ['strict', 'normal', 'lenient'],
        description: 'Threshold level for violations',
        default: 'normal',
      },
      scope: {
        type: 'string',
        enum: ['changed', 'all'],
        description: 'Analyze only changed files or all',
        default: 'all',
      },
    },
  },
};

// Threshold definitions
const THRESHOLDS = {
  strict: {
    cognitiveComplexity: 10,
    cyclomaticComplexity: 8,
    functionSize: 30,
    nestingDepth: 2,
    parameterCount: 3,
    fileSize: 300,
  },
  normal: {
    cognitiveComplexity: 15,
    cyclomaticComplexity: 10,
    functionSize: 50,
    nestingDepth: 3,
    parameterCount: 4,
    fileSize: 500,
  },
  lenient: {
    cognitiveComplexity: 25,
    cyclomaticComplexity: 15,
    functionSize: 80,
    nestingDepth: 4,
    parameterCount: 5,
    fileSize: 800,
  },
};

export async function handleAnalyzeCodeQuality(
  args: unknown,
  config: Config
): Promise<string> {
  const input = AnalyzeCodeQualityInputSchema.parse(args);
  const projectPath = input.path || config.smartstack.projectPath;
  const thresholdLevel = input.threshold;
  const thresholds = THRESHOLDS[thresholdLevel];
  const requestedMetrics = input.metrics || ['all'];
  const analyzeAll = requestedMetrics.includes('all');

  logger.info('Analyzing code quality', { projectPath, threshold: thresholdLevel });

  const structure = await findSmartStackStructure(projectPath);

  // Collect all function metrics and file contents
  const allFunctionMetrics: FunctionMetrics[] = [];
  const fileMetrics: Map<string, { lineCount: number; functions: number }> = new Map();
  const fileContents: Map<string, string> = new Map();
  const allUnusedMembers: UnusedMember[] = [];
  const allUnassignedFields: UnassignedField[] = [];

  // Analyze C# files
  const csFiles = await findFiles('**/*.cs', { cwd: structure.root });
  const filteredCsFiles = csFiles.filter(f => !isExcludedPath(f));

  for (const file of filteredCsFiles) {
    const content = await readText(file);
    const relPath = path.relative(structure.root, file);
    const lineCount = content.split('\n').length;

    const functions = extractCSharpFunctions(content, relPath);
    allFunctionMetrics.push(...functions);

    fileMetrics.set(relPath, { lineCount, functions: functions.length });
    fileContents.set(relPath, content);

    // SonarCloud-style detections for C# files
    if (analyzeAll || requestedMetrics.includes('unused-members')) {
      allUnusedMembers.push(...detectUnusedMembers(content, relPath));
    }
    if (analyzeAll || requestedMetrics.includes('unassigned-fields')) {
      allUnassignedFields.push(...detectUnassignedFields(content, relPath));
    }
  }

  // Analyze TypeScript files
  const tsFiles = await findFiles('**/*.{ts,tsx}', { cwd: structure.root });
  const filteredTsFiles = tsFiles.filter(f => !isExcludedPath(f));

  for (const file of filteredTsFiles) {
    const content = await readText(file);
    const relPath = path.relative(structure.root, file);
    const lineCount = content.split('\n').length;

    const functions = extractTypeScriptFunctions(content, relPath);
    allFunctionMetrics.push(...functions);

    fileMetrics.set(relPath, { lineCount, functions: functions.length });
    fileContents.set(relPath, content);
  }

  // Calculate metrics
  const metrics = calculateMetrics(allFunctionMetrics, fileMetrics, thresholds);

  // Identify hotspots
  const hotspots = identifyHotspots(allFunctionMetrics, fileMetrics, thresholds);

  // Calculate summary
  const summary = calculateSummary(allFunctionMetrics, fileMetrics, metrics, hotspots);

  const result: CodeQualityResult = {
    summary,
    metrics,
    hotspots,
  };

  // Detect duplicated strings across all files
  let duplicatedStrings: DuplicatedString[] = [];
  if (analyzeAll || requestedMetrics.includes('duplicated-strings')) {
    duplicatedStrings = detectDuplicatedStrings(fileContents);
  }

  // Use extended report if SonarCloud detections are enabled
  if (allUnusedMembers.length > 0 || duplicatedStrings.length > 0 || allUnassignedFields.length > 0) {
    return formatExtendedReport(result, thresholds, allUnusedMembers, duplicatedStrings, allUnassignedFields);
  }

  return formatQualityReport(result, thresholds);
}

// ============================================================================
// Function Extraction
// ============================================================================

function extractCSharpFunctions(content: string, file: string): FunctionMetrics[] {
  const functions: FunctionMetrics[] = [];
  const lines = content.split('\n');

  // Pattern for C# methods - removed ^ anchor to match indented/attributed methods
  const methodPattern = /\b(public|private|protected|internal)\s+(?:async\s+)?(?:static\s+)?(?:virtual\s+)?(?:override\s+)?(?:[\w<>,\s\[\]]+)\s+(\w+)\s*\(([^)]*)\)/gm;

  let match;
  while ((match = methodPattern.exec(content)) !== null) {
    const methodName = match[2];
    const params = match[3];
    const startLine = getLineNumber(content, match.index);

    // Find method end (count braces)
    const endLine = findMethodEnd(lines, startLine - 1);
    const lineCount = endLine - startLine + 1;

    // Calculate parameters
    const parameterCount = params.trim() ? params.split(',').length : 0;

    // Extract method body for complexity analysis
    const methodBody = lines.slice(startLine - 1, endLine).join('\n');

    // Calculate complexities
    const cognitiveComplexity = calculateCognitiveComplexity(methodBody);
    const cyclomaticComplexity = calculateCyclomaticComplexity(methodBody);
    const maxNestingDepth = calculateNestingDepth(methodBody);

    functions.push({
      name: methodName,
      file,
      startLine,
      endLine,
      lineCount,
      parameterCount,
      maxNestingDepth,
      cognitiveComplexity,
      cyclomaticComplexity,
    });
  }

  return functions;
}

function extractTypeScriptFunctions(content: string, file: string): FunctionMetrics[] {
  const functions: FunctionMetrics[] = [];
  const lines = content.split('\n');
  // Track already-processed functions to avoid duplicates across patterns
  const processedFunctions = new Set<string>();

  // Pattern for TypeScript functions/methods
  const patterns = [
    // Arrow functions assigned to const/let
    /(?:const|let)\s+(\w+)\s*=\s*(?:async\s+)?\([^)]*\)\s*(?::\s*\w+)?\s*=>/g,
    // Function declarations
    /(?:async\s+)?function\s+(\w+)\s*\(([^)]*)\)/g,
    // Class methods (removed ^ anchor for consistency)
    /\b(?:async\s+)?(\w+)\s*\(([^)]*)\)\s*(?::\s*[\w<>,\s\[\]]+)?\s*\{/gm,
  ];

  for (const pattern of patterns) {
    pattern.lastIndex = 0; // Reset regex state
    let match;
    while ((match = pattern.exec(content)) !== null) {
      const funcName = match[1];
      const params = match[2] || '';
      const startLine = getLineNumber(content, match.index);

      // Skip if it's a common keyword
      if (['if', 'for', 'while', 'switch', 'catch', 'constructor'].includes(funcName)) continue;

      // Deduplicate: skip if we already processed a function at this line
      const funcKey = `${file}:${startLine}:${funcName}`;
      if (processedFunctions.has(funcKey)) continue;
      processedFunctions.add(funcKey);

      const endLine = findMethodEnd(lines, startLine - 1);
      const lineCount = endLine - startLine + 1;
      const parameterCount = params.trim() ? params.split(',').length : 0;

      const methodBody = lines.slice(startLine - 1, endLine).join('\n');
      const cognitiveComplexity = calculateCognitiveComplexity(methodBody);
      const cyclomaticComplexity = calculateCyclomaticComplexity(methodBody);
      const maxNestingDepth = calculateNestingDepth(methodBody);

      functions.push({
        name: funcName,
        file,
        startLine,
        endLine,
        lineCount,
        parameterCount,
        maxNestingDepth,
        cognitiveComplexity,
        cyclomaticComplexity,
      });
    }
  }

  return functions;
}

function findMethodEnd(lines: string[], startIndex: number): number {
  let braceCount = 0;
  let foundFirstBrace = false;

  for (let i = startIndex; i < lines.length; i++) {
    // Remove string literals and comments to avoid counting braces inside them
    const line = stripStringsAndComments(lines[i]);

    for (const char of line) {
      if (char === '{') {
        braceCount++;
        foundFirstBrace = true;
      } else if (char === '}') {
        braceCount--;
        if (foundFirstBrace && braceCount === 0) {
          return i + 1; // 1-indexed
        }
      }
    }
  }

  return startIndex + 1; // Fallback: single line
}

function stripStringsAndComments(line: string): string {
  // Remove single-line comments
  let result = line.replace(/\/\/.*$/, '');
  // Remove string literals (simple approach - handles most cases)
  result = result.replace(/"(?:[^"\\]|\\.)*"/g, '""');
  result = result.replace(/'(?:[^'\\]|\\.)*'/g, "''");
  result = result.replace(/`(?:[^`\\]|\\.)*`/g, '``');
  return result;
}

// ============================================================================
// Complexity Calculations
// ============================================================================

function calculateCognitiveComplexity(code: string): number {
  let complexity = 0;
  let nestingLevel = 0;

  const lines = code.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();

    // Count control flow structures (add 1 + nesting level)
    const controlFlowPatterns = [
      /\bif\s*\(/g,
      /\belse\s+if\s*\(/g,
      /\bfor\s*\(/g,
      /\bforeach\s*\(/g,
      /\bwhile\s*\(/g,
      /\bdo\s*\{/g,
      /\bswitch\s*\(/g,
      /\bcatch\s*\(/g,
    ];

    for (const pattern of controlFlowPatterns) {
      const matches = trimmed.match(pattern) || [];
      complexity += matches.length * (1 + nestingLevel);
    }

    // Count logical operators (each adds 1)
    const logicalPatterns = [/&&/g, /\|\|/g, /\?\?/g, /\?\./g];
    for (const pattern of logicalPatterns) {
      const matches = trimmed.match(pattern) || [];
      complexity += matches.length;
    }

    // Count ternary operators
    const ternaryMatches = trimmed.match(/\?[^?:]+:/g) || [];
    complexity += ternaryMatches.length;

    // Track nesting level
    const openBraces = (trimmed.match(/\{/g) || []).length;
    const closeBraces = (trimmed.match(/\}/g) || []).length;
    nestingLevel += openBraces - closeBraces;
    nestingLevel = Math.max(0, nestingLevel);
  }

  return complexity;
}

function calculateCyclomaticComplexity(code: string): number {
  let complexity = 1; // Base complexity

  const patterns = [
    /\bif\b/g,
    /\belse\s+if\b/g,
    /\bwhile\b/g,
    /\bfor\b/g,
    /\bforeach\b/g,
    /\bcase\b/g,
    /\bcatch\b/g,
    /\?\?/g,
    /\?\./g,
    /&&/g,
    /\|\|/g,
  ];

  for (const pattern of patterns) {
    const matches = code.match(pattern) || [];
    complexity += matches.length;
  }

  return complexity;
}

function calculateNestingDepth(code: string): number {
  let maxDepth = 0;
  let currentDepth = 0;

  for (const char of code) {
    if (char === '{') {
      currentDepth++;
      maxDepth = Math.max(maxDepth, currentDepth);
    } else if (char === '}') {
      currentDepth = Math.max(0, currentDepth - 1);
    }
  }

  return maxDepth;
}

// ============================================================================
// Metrics Calculation
// ============================================================================

function calculateMetrics(
  functions: FunctionMetrics[],
  fileMetrics: Map<string, { lineCount: number; functions: number }>,
  thresholds: typeof THRESHOLDS.normal
): MetricResults {
  const createStat = (
    values: number[],
    threshold: number
  ): MetricStat => {
    const filtered = values.filter(v => v > 0);
    const avg = filtered.length > 0 ? filtered.reduce((a, b) => a + b, 0) / filtered.length : 0;
    const max = filtered.length > 0 ? Math.max(...filtered) : 0;
    const violations = filtered.filter(v => v > threshold).length;

    return {
      average: Math.round(avg * 10) / 10,
      max,
      threshold,
      violations,
    };
  };

  const fileSizes = Array.from(fileMetrics.values()).map(f => f.lineCount);

  return {
    cognitiveComplexity: createStat(
      functions.map(f => f.cognitiveComplexity),
      thresholds.cognitiveComplexity
    ),
    cyclomaticComplexity: createStat(
      functions.map(f => f.cyclomaticComplexity),
      thresholds.cyclomaticComplexity
    ),
    functionSize: createStat(
      functions.map(f => f.lineCount),
      thresholds.functionSize
    ),
    nestingDepth: createStat(
      functions.map(f => f.maxNestingDepth),
      thresholds.nestingDepth
    ),
    fileSize: createStat(fileSizes, thresholds.fileSize),
  };
}

function identifyHotspots(
  functions: FunctionMetrics[],
  fileMetrics: Map<string, { lineCount: number; functions: number }>,
  thresholds: typeof THRESHOLDS.normal
): Hotspot[] {
  const hotspots: Hotspot[] = [];

  // Function-level hotspots
  for (const func of functions) {
    const issues: string[] = [];

    if (func.cognitiveComplexity > thresholds.cognitiveComplexity) {
      issues.push(`Cognitive complexity: ${func.cognitiveComplexity} (threshold: ${thresholds.cognitiveComplexity})`);
    }
    if (func.cyclomaticComplexity > thresholds.cyclomaticComplexity) {
      issues.push(`Cyclomatic complexity: ${func.cyclomaticComplexity} (threshold: ${thresholds.cyclomaticComplexity})`);
    }
    if (func.lineCount > thresholds.functionSize) {
      issues.push(`Lines: ${func.lineCount} (threshold: ${thresholds.functionSize})`);
    }
    if (func.maxNestingDepth > thresholds.nestingDepth) {
      issues.push(`Nesting depth: ${func.maxNestingDepth} (threshold: ${thresholds.nestingDepth})`);
    }

    if (issues.length > 0) {
      const severity = issues.length >= 3 ? 'high' : issues.length === 2 ? 'medium' : 'low';
      hotspots.push({
        file: func.file,
        function: func.name,
        issues,
        severity,
        metrics: {
          cognitiveComplexity: func.cognitiveComplexity,
          cyclomaticComplexity: func.cyclomaticComplexity,
          lineCount: func.lineCount,
          nestingDepth: func.maxNestingDepth,
        },
      });
    }
  }

  // File-level hotspots
  for (const [file, metrics] of fileMetrics) {
    if (metrics.lineCount > thresholds.fileSize) {
      hotspots.push({
        file,
        issues: [`File size: ${metrics.lineCount} lines (threshold: ${thresholds.fileSize})`],
        severity: 'medium',
        metrics: {
          lineCount: metrics.lineCount,
        },
      });
    }
  }

  // Sort by severity
  const severityOrder = { high: 0, medium: 1, low: 2 };
  hotspots.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return hotspots.slice(0, 20); // Limit to top 20
}

function calculateSummary(
  functions: FunctionMetrics[],
  fileMetrics: Map<string, { lineCount: number; functions: number }>,
  metrics: MetricResults,
  hotspots: Hotspot[]
): QualitySummary {
  const totalViolations =
    metrics.cognitiveComplexity.violations +
    metrics.cyclomaticComplexity.violations +
    metrics.functionSize.violations +
    metrics.nestingDepth.violations +
    metrics.fileSize.violations;

  const totalFunctions = functions.length;
  const totalFiles = fileMetrics.size;

  // Calculate score (100 - penalty for violations)
  const violationRate = totalFunctions > 0 ? totalViolations / totalFunctions : 0;
  const score = Math.max(0, Math.round(100 - violationRate * 100));

  // Calculate grade
  let grade: 'A' | 'B' | 'C' | 'D' | 'F';
  if (score >= 90) grade = 'A';
  else if (score >= 80) grade = 'B';
  else if (score >= 70) grade = 'C';
  else if (score >= 60) grade = 'D';
  else grade = 'F';

  return {
    score,
    grade,
    filesAnalyzed: totalFiles,
    functionsAnalyzed: totalFunctions,
    issuesFound: hotspots.length,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function isExcludedPath(filePath: string): boolean {
  const exclusions = [
    /[/\\]bin[/\\]/,
    /[/\\]obj[/\\]/,
    /[/\\]node_modules[/\\]/,
    /[/\\]Migrations[/\\]/,
    /\.test\./,
    /\.spec\./,
    /Tests[/\\]/,
    /\.d\.ts$/,
    /\.min\./,
  ];

  return exclusions.some(pattern => pattern.test(filePath));
}

function getLineNumber(content: string, index: number): number {
  // Handle both LF and CRLF line endings
  const normalizedContent = content.substring(0, index).replace(/\r\n/g, '\n');
  return normalizedContent.split('\n').length;
}

function formatQualityReport(
  result: CodeQualityResult,
  thresholds: typeof THRESHOLDS.normal
): string {
  const lines: string[] = [];

  lines.push('# Code Quality Report');
  lines.push('');
  lines.push('## Summary');
  lines.push(`- **Score**: ${result.summary.score}/100 (Grade: ${result.summary.grade})`);
  lines.push(`- **Files analyzed**: ${result.summary.filesAnalyzed}`);
  lines.push(`- **Functions analyzed**: ${result.summary.functionsAnalyzed}`);
  lines.push(`- **Issues found**: ${result.summary.issuesFound}`);
  lines.push('');

  lines.push('## Metrics Overview');
  lines.push('');
  lines.push('| Metric | Average | Max | Threshold | Status |');
  lines.push('|--------|---------|-----|-----------|--------|');

  const formatMetricRow = (name: string, stat: MetricStat) => {
    const status = stat.violations > 0 ? `${stat.violations} violations` : '✅ OK';
    return `| ${name} | ${stat.average} | ${stat.max} | ${stat.threshold} | ${status} |`;
  };

  lines.push(formatMetricRow('Cognitive Complexity', result.metrics.cognitiveComplexity));
  lines.push(formatMetricRow('Cyclomatic Complexity', result.metrics.cyclomaticComplexity));
  lines.push(formatMetricRow('Function Size', result.metrics.functionSize));
  lines.push(formatMetricRow('Nesting Depth', result.metrics.nestingDepth));
  lines.push(formatMetricRow('File Size', result.metrics.fileSize));
  lines.push('');

  if (result.hotspots.length > 0) {
    lines.push('## Hotspots (Needs Attention)');
    lines.push('');

    for (const hotspot of result.hotspots) {
      const severityEmoji = hotspot.severity === 'high' ? '🔴' : hotspot.severity === 'medium' ? '🟡' : '🟢';
      const location = hotspot.function
        ? `\`${hotspot.function}\` (${hotspot.file})`
        : `\`${hotspot.file}\``;

      lines.push(`### ${severityEmoji} ${hotspot.severity.toUpperCase()}: ${location}`);
      for (const issue of hotspot.issues) {
        lines.push(`- ${issue}`);
      }

      // Add recommendation based on issues
      if (hotspot.function) {
        if (hotspot.metrics.cognitiveComplexity && hotspot.metrics.cognitiveComplexity > thresholds.cognitiveComplexity) {
          lines.push(`- **Recommendation**: Extract logic into smaller, focused methods`);
        } else if (hotspot.metrics.lineCount && hotspot.metrics.lineCount > thresholds.functionSize) {
          lines.push(`- **Recommendation**: Split into multiple functions with single responsibilities`);
        }
      } else {
        lines.push(`- **Recommendation**: Consider splitting this file into smaller modules`);
      }
      lines.push('');
    }
  } else {
    lines.push('No hotspots found. Code quality is within acceptable thresholds.');
  }

  return lines.join('\n');
}

// ============================================================================
// SonarCloud-inspired Detections (S1144, S1192, S3459)
// ============================================================================

/**
 * Detect unused private members (S1144)
 * Finds private methods and fields that are never referenced in the same file
 */
function detectUnusedMembers(content: string, file: string): UnusedMember[] {
  const unusedMembers: UnusedMember[] = [];

  // Find private methods in C#
  const privateMethodPattern = /\b(private|internal)\s+(?:async\s+)?(?:static\s+)?[\w<>,\s\[\]]+\s+(\w+)\s*\(/gm;
  let match;

  while ((match = privateMethodPattern.exec(content)) !== null) {
    const visibility = match[1] as 'private' | 'internal';
    const methodName = match[2];
    const line = getLineNumber(content, match.index);

    // Skip constructors, property accessors, and common patterns
    if (['get', 'set', 'Dispose', 'InitializeComponent'].includes(methodName)) continue;

    // Count occurrences of the method name (excluding the declaration itself)
    const regex = new RegExp(`\\b${methodName}\\b`, 'g');
    const occurrences = (content.match(regex) || []).length;

    // If only 1 occurrence (the declaration), it's unused
    if (occurrences === 1) {
      unusedMembers.push({
        name: methodName,
        type: 'method',
        visibility,
        file,
        line,
        rule: 'S1144',
      });
    }
  }

  // Find private fields in C#
  const privateFieldPattern = /\b(private|internal)\s+(?:readonly\s+)?(?:static\s+)?[\w<>,\[\]]+\s+_?(\w+)\s*[;=]/gm;

  while ((match = privateFieldPattern.exec(content)) !== null) {
    const visibility = match[1] as 'private' | 'internal';
    const fieldName = match[2];
    const line = getLineNumber(content, match.index);

    // Count occurrences
    const regex = new RegExp(`\\b${fieldName}\\b`, 'g');
    const occurrences = (content.match(regex) || []).length;

    if (occurrences === 1) {
      unusedMembers.push({
        name: fieldName,
        type: 'field',
        visibility,
        file,
        line,
        rule: 'S1144',
      });
    }
  }

  return unusedMembers;
}

/**
 * Detect duplicated string literals (S1192)
 * Finds string literals that appear 3+ times and should be constants
 */
function detectDuplicatedStrings(
  fileContents: Map<string, string>
): DuplicatedString[] {
  const stringOccurrences = new Map<string, Array<{ file: string; line: number }>>();

  for (const [file, content] of fileContents) {
    // Find all string literals (double-quoted)
    const stringPattern = /"([^"\\]|\\.){5,}"/g;
    let match;

    while ((match = stringPattern.exec(content)) !== null) {
      const literal = match[0];
      const line = getLineNumber(content, match.index);

      // Skip common patterns that shouldn't be constants
      if (isIgnoredStringLiteral(literal)) continue;

      if (!stringOccurrences.has(literal)) {
        stringOccurrences.set(literal, []);
      }
      stringOccurrences.get(literal)!.push({ file, line });
    }
  }

  // Filter to only those with 3+ occurrences
  const duplicated: DuplicatedString[] = [];

  for (const [literal, locations] of stringOccurrences) {
    if (locations.length >= 3) {
      duplicated.push({
        literal,
        occurrences: locations.length,
        locations: locations.slice(0, 5), // Limit to 5 examples
        suggestedConstantName: suggestConstantName(literal),
        rule: 'S1192',
      });
    }
  }

  // Sort by occurrences descending
  duplicated.sort((a, b) => b.occurrences - a.occurrences);

  return duplicated.slice(0, 10); // Limit to top 10
}

function isIgnoredStringLiteral(literal: string): boolean {
  const ignoredPatterns = [
    /^"\s*"$/,           // Empty or whitespace
    /^"[,.\-:;\/\\]+"$/, // Punctuation only
    /^"https?:\/\//,     // URLs
    /^"[a-z]{1,4}:"$/i,  // Protocol prefixes
    /^"\{[0-9]+\}"$/,    // Format placeholders
    /^"[a-z_]+:[a-z_]+"$/i, // Resource keys
  ];

  return ignoredPatterns.some(p => p.test(literal));
}

function suggestConstantName(literal: string): string {
  // Remove quotes and convert to constant name
  const content = literal.slice(1, -1);

  // Take first few words
  const words = content
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 0)
    .slice(0, 4);

  if (words.length === 0) return 'STRING_CONSTANT';

  return words
    .map(w => w.toUpperCase())
    .join('_');
}

/**
 * Detect unassigned fields (S3459)
 * Finds fields that are declared but never assigned a value
 */
function detectUnassignedFields(content: string, file: string): UnassignedField[] {
  const unassignedFields: UnassignedField[] = [];

  // Find field declarations without initializers
  const fieldPattern = /\b(private|protected|internal|public)\s+(?:readonly\s+)?(\w+(?:<[^>]+>)?)\s+(\w+)\s*;/gm;
  let match;

  while ((match = fieldPattern.exec(content)) !== null) {
    const fieldType = match[2];
    const fieldName = match[3];
    const line = getLineNumber(content, match.index);

    // Check if the field is ever assigned (look for "fieldName = " or "fieldName=")
    const assignmentPattern = new RegExp(`\\b${fieldName}\\s*=`, 'g');
    const assignments = content.match(assignmentPattern) || [];

    // Check constructor assignments
    const constructorAssignmentPattern = new RegExp(`this\\.${fieldName}\\s*=`, 'g');
    const constructorAssignments = content.match(constructorAssignmentPattern) || [];

    // If no assignments found, it's unassigned
    if (assignments.length === 0 && constructorAssignments.length === 0) {
      unassignedFields.push({
        name: fieldName,
        type: fieldType,
        file,
        line,
        rule: 'S3459',
      });
    }
  }

  return unassignedFields;
}

// ============================================================================
// Extended Report Formatting
// ============================================================================

function formatExtendedReport(
  result: CodeQualityResult,
  thresholds: typeof THRESHOLDS.normal,
  unusedMembers: UnusedMember[],
  duplicatedStrings: DuplicatedString[],
  unassignedFields: UnassignedField[]
): string {
  // Start with base report
  let report = formatQualityReport(result, thresholds);

  // Add SonarCloud-inspired detections
  if (unusedMembers.length > 0 || duplicatedStrings.length > 0 || unassignedFields.length > 0) {
    report += '\n\n## SonarCloud-Style Detections\n';

    if (unusedMembers.length > 0) {
      report += '\n### 🔍 Unused Private Members (S1144)\n';
      report += `Found ${unusedMembers.length} unused private members:\n\n`;
      for (const member of unusedMembers.slice(0, 10)) {
        report += `- \`${member.name}\` (${member.type}) in \`${member.file}:${member.line}\`\n`;
      }
      if (unusedMembers.length > 10) {
        report += `\n... and ${unusedMembers.length - 10} more\n`;
      }
    }

    if (duplicatedStrings.length > 0) {
      report += '\n### 🔄 Duplicated String Literals (S1192)\n';
      report += `Found ${duplicatedStrings.length} duplicated strings (3+ occurrences):\n\n`;
      for (const dup of duplicatedStrings) {
        const truncated = dup.literal.length > 50
          ? dup.literal.slice(0, 47) + '..."'
          : dup.literal;
        report += `- ${truncated} (${dup.occurrences}x) → suggested: \`${dup.suggestedConstantName}\`\n`;
      }
    }

    if (unassignedFields.length > 0) {
      report += '\n### ⚠️ Unassigned Fields (S3459)\n';
      report += `Found ${unassignedFields.length} fields that are never assigned:\n\n`;
      for (const field of unassignedFields.slice(0, 10)) {
        report += `- \`${field.type} ${field.name}\` in \`${field.file}:${field.line}\`\n`;
      }
      if (unassignedFields.length > 10) {
        report += `\n... and ${unassignedFields.length - 10} more\n`;
      }
    }
  }

  return report;
}
